import React, { useEffect, useState, useMemo, useCallback } from "react";
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { Container, Row, Col } from 'reactstrap';
import { api } from "../../config";
import DashboardEstandes from './componente';
import { FeiraProvider, useFeira } from "../../Contexts/FeiraContext";
import { fetchComAuth, fetchComAuthJson } from '../../common/api';

const Dashboard = () => {
    const [feirasDisponiveis, setFeirasDisponiveis] = useState([]);
    const [feiraSelecionada, setFeiraSelecionada] = useState(null);
    const [permissaoPerfil, setPermissaoPerfil] = useState([]);
    const [dados, setDados] = useState(null);
    const [estandesUnicos, setEstandesUnicos] = useState(null);
    const [cancelamentos, setCancelamentos] = useState([]);
    const [reservas, setReservas] = useState(null);
    const [isLoadingFeiras, setIsLoadingFeiras] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [feiraRenovada, setFeiraRenovada] = useState(false);
    const [error, setError] = useState(null);
    
    const [paises, setPaises] = useState([]);
    const [estados, setEstados] = useState([]);
    const [cidades, setCidades] = useState([]);
    const [pavilhoes, setPavilhoes] = useState([]);
    const [pavilhoesTotais, setTotalPavilhoes] = useState([]);
    
    const [authUser, setAuthUser] = useState(() => {
        try {
            return JSON.parse(sessionStorage.getItem("authUser")) || {};
        } catch (e) {
            return {};
        }
    });

    const userData = useMemo(() => authUser.user || authUser, [authUser]);
    const userType = useMemo(() => userData.tipo || '', [userData]);
    const userFeirasIds = userData.feiras?.map(item => item.feira_id) || [];
    
    const [usuarioLogado, setUsuarioLogado] = useState({
        id: userData.id,
        nome: userData.name,
        email: userData.email,
        perfil: userData.tipo,
        feiras: userFeirasIds
    });

    document.title = "Dashboard | EVOMAP - Tecnologia que conecta negócios";

    const fetchPaises = async () => {
        try {
            const response = await fetchComAuth(`${api.API_URL}/paises`, { 
                method: 'GET' 
            });
            if (response.ok) {
                const data = await response.json();
                setPaises(data.data || []);
            }
        } catch (error) {
            console.error("Erro ao buscar países:", error);
        }
    };

    const fetchPavilhoes = async (feiraId) => {
        try {
            const response = await fetchComAuth(`${api.API_URL}/feira/pavilhao_feira/${feiraId}`, { 
                method: 'GET' 
            });

            if (response.ok) {
                const data = await response.json();
                setPavilhoes(data.data || []);
            }
        } catch (error) {
            console.error("Erro ao buscar pavilhões:", error);
        }
    };

    const fetchEstados = useCallback(async () => {
        try {
            const response = await fetch(`${api.API_URL}/estados`, {
                headers: { chave: "18266602-6085-4532-992d-7bc2750b19f4" }
            });
            if (response.ok) {
                const data = await response.json();
                setEstados(data.data || []);
            }
        } catch (error) {
            console.error("Erro ao buscar estados:", error);
        }
    });

    const fetchCidades = useCallback(async (estadoSigla) => {
    try {
        const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${estadoSigla}/municipios`);
        
        if (response.ok) {
            const cidades = await response.json();
            setCidades(cidades.map(c => ({ id: c.id, nome: c.nome })) || []);
        }
    } catch (error) {
        console.error("Erro ao buscar cidades:", error);
    }
}, []);

    const fetchTotalPavilhao = async (feiraId) => {
        try {
            const response = await fetchComAuth(`${api.API_URL}/dashboard/pavilhao/${feiraId}`, { 
                method: 'GET' 
            });

            if (response.ok) {
                const data = await response.json();
                setTotalPavilhoes(data.data || []);
            }
        } catch (error) {
            console.error("Erro ao buscar totais pavilhões:", error);
        }
    };

    useEffect(() => {
        const fetchFeiras = async () => {
            setIsLoadingFeiras(true);
            try {
            
                const response = await fetchComAuthJson(`${api.API_URL}/feiras`, {
                    method: 'GET'
                });

                if (!response.ok) throw new Error("Erro na resposta da API");
                
                const data = await response.json();
                
                if (data && data.data) {
                    const feiras = ['JOTAPIX', 'EVOMAP'].includes(userType) ?
                        data.data : data.data.filter(f => f.id && usuarioLogado.feiras.includes(f.id));
       
                    setFeirasDisponiveis(feiras);

                    if (feiras.length === 1) {
                        const feira = feiras[0];
                        setFeiraSelecionada(feira.id.toString());
                        setFeiraRenovada(feira.renovacao || false);
                    }
                }
 
                fetchPaises();
                
            } catch (error) {
                console.error("Erro ao buscar feiras:", error);
                setError("Erro ao carregar lista de feiras");
            } finally {
                setIsLoadingFeiras(false);
            }
        };

        fetchFeiras();
    }, [userType, usuarioLogado.feiras]);

    useEffect(() => {
        if (!feiraSelecionada) return;

        // Buscar pavilhões da feira selecionada
        fetchPavilhoes(feiraSelecionada);

        // Buscar dados do dashboard
        const fetchDadosFeira = async () => {
            setIsLoading(true);
            setDados(null);
            setEstandesUnicos(null);

            try {
                const response = await fetchComAuth(`${api.API_URL}/dashboard/${feiraSelecionada}`, {
                    method: 'GET'
                });

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                
                const data = await response.json();
                
                if (!data) throw new Error("Dados vazios recebidos da API");
                
                const registrosRecebidos = data.data.dados || [];

                const cancelamentosRecebidos = Array.isArray(data.data.cancelados)
                    ? data.data.cancelados
                    : registrosRecebidos.filter(item =>
                        item.tipo_registro === 'CANCELAMENTO' ||
                        item.contabiliza_inventario === false
                    );

                const registrosInventario = registrosRecebidos.filter(item =>
                    item.tipo_registro !== 'CANCELAMENTO' &&
                    item.contabiliza_inventario !== false
                );

                setDados(registrosInventario);

                // A deduplicação é aplicada somente ao inventário atual.
                // O histórico de cancelamentos não pode ser deduplicado por estande_id,
                // pois um mesmo estande pode ter mais de um evento de cancelamento.
                const listaUnica = Array.from(
                    new Map(registrosInventario.map(item => [item.id, item])).values()
                );

                setEstandesUnicos(listaUnica);
                setCancelamentos(cancelamentosRecebidos);
                
                if (data.data.permissao && ['COMERCIAL', 'ORGANIZACAO'].includes(userType)) {
                    const acessos = data.data.permissao.filter((f) => f.chave_perfil === userType);
                    const permissao = acessos.map(a => a.dashboard_permissao);
                    setPermissaoPerfil(acessos.map(a => a.dashboard_permissao));

                    if (permissao.some(p => p.chave === 'PAVILHOES')){
                        fetchTotalPavilhao(feiraSelecionada);
                    }
                }
                else {
                    fetchTotalPavilhao(feiraSelecionada);
                }
               
                setError(null);
            } catch (error) {
                console.error("Erro ao buscar dados da feira:", error);
                setError(`Erro ao carregar dados: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        };

        // Buscar reservas
        const fetchReservas = async () => {
            setIsLoading(true);
            setReservas(null);
            
            try {
                const response = await fetchComAuth(`${api.API_URL}/dashboard/reservas/${feiraSelecionada}`, {
                    method: 'GET'
                });

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                
                const data = await response.json();
                
                if (!data) throw new Error("Dados vazios recebidos da API");
                
                setReservas(data.data || []);
                setError(null);
                
            } catch (error) {
                console.error("Erro ao buscar dados das reservas:", error);
                setError(`Erro ao carregar dados: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDadosFeira();
        fetchReservas();
    }, [feiraSelecionada, userType]);

    const handleSelecionarFeira = (e) => {
        const value = e.target.value;
        
        setFeiraSelecionada(value || null);
        
        const feiraEncontrada = feirasDisponiveis.find(f => f.id == value);
        if (feiraEncontrada) {
            setFeiraRenovada(feiraEncontrada.renovacao || false);
            
            // Limpar dados anteriores enquanto carrega os novos
            setDados(null);
            setEstandesUnicos(null);
            setReservas(null);
            setCancelamentos([]);
            setPavilhoes([]);
            setPermissaoPerfil([]);
            setTotalPavilhoes([]);
        } else {
            setFeiraRenovada(false);
        }
    };

    return (
        <div className="page-content">
            
            <BreadCrumb title="Dashboard" pageTitle="Feiras" />
            
            <Row className="mb-3">
                <Col md={6}>
                    <label className="form-label" id="label_feira" htmlFor="feira-select-dash">Selecione a Feira</label>
                    <select
                        id="feira-select-dash" name="feira_id_dash"
                        className="form-select"
                        value={feiraSelecionada || ''}
                        onChange={handleSelecionarFeira}
                        disabled={isLoadingFeiras}
                    >
                        <option value="">Selecione...</option>
                        {feirasDisponiveis.map(feira => (
                            <option key={feira.id} value={feira.id.toString()}>
                                {feira.nome}
                            </option>
                        ))}
                    </select>
                </Col>
            </Row>

            {isLoading && (
                <div className="text-center my-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p>Carregando dados da feira...</p>
                </div>
            )}

            {error && (
                <div className="alert alert-danger my-3">
                    {error}
                </div>
            )}

            {!isLoading && feiraSelecionada && dados && (
                <FeiraProvider idFeira={feiraSelecionada}>
                    <DashboardEstandes 
                        reservas={reservas}
                        estandes={estandesUnicos}
                        cancelamentos={cancelamentos}
                        feiraId={feiraSelecionada}
                        mostrarTodosComerciais={['JOTAPIX', 'EVOMAP'].includes(usuarioLogado.perfil)}
                        feiraSelecionada={feiraSelecionada}
                        permissao={permissaoPerfil}
                        perfil={usuarioLogado.perfil}
                        feiraRenovada={feiraRenovada}
                        pavilhoesTotais={pavilhoesTotais}
                        paises={paises}
                        estados={estados}
                        cidades={cidades}
                        pavilhoes={pavilhoes}
                        onFetchEstados={fetchEstados}
                        onFetchCidades={fetchCidades}
                    />
                </FeiraProvider>
            )}

                {!isLoading && feiraSelecionada && Array.isArray(dados) && dados.length === 0 && (
                    <div className="alert alert-warning my-3">
                        Nenhum espaço cadastrado para esta feira.
                    </div>
                )}
      
        </div>
    );
};

export default Dashboard;